export const INDICATOR_COMPLETED_PAGE_SIZE = 200;

export const fetchAllCompletedIndicators = async (
  apiClient,
  {
    limitDays = 365,
    pageSize = INDICATOR_COMPLETED_PAGE_SIZE,
    onProgress,
  } = {}
) => {
  if (!apiClient || typeof apiClient.get !== "function") {
    throw new Error("Cliente da API inválido para carregar indicadores");
  }

  const normalizedLimitDays = Math.min(Math.max(Math.floor(Number(limitDays)) || 365, 1), 365);
  const normalizedPageSize = Math.min(
    Math.max(Math.floor(Number(pageSize)) || INDICATOR_COMPLETED_PAGE_SIZE, 1),
    INDICATOR_COMPLETED_PAGE_SIZE
  );
  const items = [];
  const seenIds = new Set();
  let cursor = 0;
  let snapshotAt = "";
  let pageNumber = 0;

  while (true) {
    const params = {
      limitDays: normalizedLimitDays,
      limit: normalizedPageSize,
      cursor,
    };
    if (snapshotAt) params.snapshotAt = snapshotAt;

    const response = await apiClient.get("/processes/instances/completed/analytics", { params });
    const payload = response?.data;
    if (!payload || !Array.isArray(payload.items)) {
      throw new Error("Resposta inválida ao carregar o histórico de conclusões");
    }

    if (!snapshotAt && typeof payload.snapshotAt === "string" && payload.snapshotAt.trim()) {
      snapshotAt = payload.snapshotAt;
    }

    payload.items.forEach((item) => {
      const id = item?.id;
      if (id === null || id === undefined || id === "") {
        items.push(item);
        return;
      }
      const key = String(id);
      if (seenIds.has(key)) return;
      seenIds.add(key);
      items.push(item);
    });

    pageNumber += 1;
    if (typeof onProgress === "function") {
      onProgress({ loaded: items.length, pages: pageNumber });
    }

    if (payload.hasMore !== true) break;

    const nextCursor = Number(payload.nextCursor);
    if (!Number.isInteger(nextCursor) || nextCursor <= cursor) {
      throw new Error("Paginação inválida no histórico de conclusões");
    }
    cursor = nextCursor;
  }

  return items;
};

const asValidDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizePeriodDays = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 90;
  return Math.min(Math.max(Math.floor(numeric), 1), 365);
};

export const getIndicatorProcessValue = (item) => {
  const process = item?.process || item || {};
  const id = process?.id ?? item?.processId;
  if (id !== null && id !== undefined && String(id).trim()) {
    return `id:${String(id).trim()}`;
  }

  const name = process?.name ?? item?.processName;
  if (typeof name === "string" && name.trim()) {
    return `name:${name.trim().toLocaleLowerCase("pt-BR")}`;
  }

  return "unknown";
};

const getIndicatorProcessLabel = (item) => {
  const process = item?.process || item || {};
  const name = process?.name ?? item?.processName;
  if (typeof name === "string" && name.trim()) return name.trim();
  const id = process?.id ?? item?.processId;
  return id === null || id === undefined ? "Processo não identificado" : `Processo #${id}`;
};

export const buildIndicatorProcessOptions = (processes = [], instances = []) => {
  const options = new Map();

  [...processes, ...instances].forEach((item) => {
    const value = getIndicatorProcessValue(item);
    if (value === "unknown" || options.has(value)) return;
    options.set(value, getIndicatorProcessLabel(item));
  });

  return [
    { value: "all", label: "Todos os processos" },
    ...Array.from(options, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR")
    ),
  ];
};

const matchesProcess = (item, processValue) =>
  !processValue ||
  processValue === "all" ||
  getIndicatorProcessValue(item) === processValue;

const periodStart = (periodDays, now) => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (normalizePeriodDays(periodDays) - 1));
  return start.getTime();
};

const isInsidePeriod = (item, startTime, keys) => {
  for (const key of keys) {
    const parsed = asValidDate(item?.[key]);
    if (parsed) return parsed.getTime() >= startTime;
  }
  return false;
};

export const buildIndicatorDataset = ({
  openItems = [],
  completedItems = [],
  processValue = "all",
  periodDays = 90,
  status = "all",
  now = new Date(),
} = {}) => {
  const safeNow = asValidDate(now) || new Date();
  const startTime = periodStart(periodDays, safeNow);

  const filteredOpenItems = openItems.filter((item) => matchesProcess(item, processValue));
  const openInPeriod = filteredOpenItems.filter((item) =>
    isInsidePeriod(item, startTime, ["startedAt"])
  );
  const completedInPeriod = completedItems.filter(
    (item) =>
      matchesProcess(item, processValue) &&
      isInsidePeriod(item, startTime, ["endedAt", "startedAt"])
  );

  const allTrackedInstances =
    status === "open"
      ? openInPeriod
      : status === "completed"
        ? completedInPeriod
        : [...openInPeriod, ...completedInPeriod];

  const nowTime = safeNow.getTime();
  const overdueOpenCount = filteredOpenItems.filter((item) => {
    const deadline = asValidDate(item?.currentStepDeadline);
    return deadline ? deadline.getTime() < nowTime : false;
  }).length;

  const completionBase = completedInPeriod.length + filteredOpenItems.length;
  const completionRate = completionBase
    ? Math.round((completedInPeriod.length / completionBase) * 100)
    : 0;

  const durations = completedInPeriod
    .map((item) => {
      const startedAt = asValidDate(item?.startedAt);
      const endedAt = asValidDate(item?.endedAt);
      if (!startedAt || !endedAt) return null;
      return Math.max(endedAt.getTime() - startedAt.getTime(), 0);
    })
    .filter((duration) => duration !== null);

  const averageCompletionMs = durations.length
    ? durations.reduce((total, duration) => total + duration, 0) / durations.length
    : null;

  return {
    allTrackedInstances,
    completedInPeriod,
    filteredOpenItems,
    openInPeriod,
    overdueOpenCount,
    completionRate,
    averageCompletionMs,
  };
};

export const buildStatusDonutBackground = (items = []) => {
  const total = items.reduce((sum, item) => sum + Math.max(Number(item?.value) || 0, 0), 0);
  if (!total) return "conic-gradient(#e2e8f0 0% 100%)";

  let cursor = 0;
  const segments = items
    .filter((item) => Number(item?.value) > 0)
    .map((item) => {
      const start = cursor;
      cursor += (Number(item.value) / total) * 100;
      return `${item.hex || "#94a3b8"} ${start}% ${cursor}%`;
    });

  return `conic-gradient(${segments.join(", ")})`;
};
